import { Unpacked } from '@mi-front/core';
import { AsyncView } from '../../../utils/async-view/AsyncView';
import { Form, FormProps } from '../../form/Form';
import { Modal, ModalProps } from '../base/Modal';
import { useCallback } from 'react';

type Props<Values> = Omit<FormProps<Unpacked<Values>>, 'initialValues' | 'onSubmit'> &
  Pick<ModalProps, 'show' | 'title' | 'onHide'> & {
    initialValues: Values | (() => Promise<Values>);
    onSubmit: (data: Unpacked<Values>) => Promise<any> | void;
  };

export type FormModalProps<Values> = Props<Values>;

export function FormModal<Values>(props: Props<Values>) {
  const { initialValues, validationSchema, onSubmit, children, onHide, title, show, ...rest } =
    props;

  return (
    <Modal onHide={onHide} title={title} show={show} showCloseButton={false} noBody data-cy="modal">
      <AsyncView
        // wrapped in useCallback to avoid re rendering AsyncView
        // when the initialValues object shallowly changes
        promiseFn={useCallback(() => {
          if (typeof initialValues === 'function') {
            return (initialValues as () => Promise<Values>)();
          }

          return Promise.resolve(initialValues);
        }, [JSON.stringify(initialValues)])}
      >
        {(pp) => {
          return (
            <Form
              initialValues={pp.data as Unpacked<Values>}
              validationSchema={validationSchema}
              onSubmit={async (data) => {
                await onSubmit(data);
              }}
              onCancel={() => {
                onHide();
              }}
              actions={(_formikProps, defaultActions) => (
                <Modal.Footer>{defaultActions}</Modal.Footer>
              )}
              {...rest}
            >
              {(formikProps) => (
                <Modal.Body>
                  {typeof children === 'function' ? children(formikProps) : children}
                </Modal.Body>
              )}
            </Form>
          );
        }}
      </AsyncView>
    </Modal>
  );
}
